// src/components/Admin/Cantieri/MaterialiFissiManagement.jsx
import React, { useState, useEffect } from 'react';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDocs, 
  query, 
  orderBy 
} from 'firebase/firestore';
import { db } from '../../../firebase';
import './MasterDataManagement.css';
import './MaterialiFissiManagement.css';

const MaterialiFissiManagement = () => {
  const [materiali, setMateriali] = useState([]);
  const [filteredMateriali, setFilteredMateriali] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [formData, setFormData] = useState({
    nomeMateriale: '',
    prezzoUnitario: '',
    unitaMisura: 'tn'
  });

  // Unità di misura disponibili
  const unitaMisuraOptions = [
    { value: 'tn', label: 'Tonnellate (tn)' },
    { value: 'kg', label: 'Chilogrammi (kg)' },
    { value: 'nm', label: 'Numero (nm)' },
    { value: 'l', label: 'Litri (l)' }
  ];

  // Lista materiali iniziali come specificato
  const materialiIniziali = [
    { nomeMateriale: 'Emulsione Bituminosa', prezzoUnitario: 0.80, unitaMisura: 'kg' },
    { nomeMateriale: 'Congl.Bituminoso di tipo Base', prezzoUnitario: 66.00, unitaMisura: 'tn' },
    { nomeMateriale: 'Congl.Bituminoso di tipo Binder', prezzoUnitario: 68.00, unitaMisura: 'tn' },
    { nomeMateriale: 'Congl.Bituminoso di tipo Binder Hard', prezzoUnitario: 76.00, unitaMisura: 'tn' },
    { nomeMateriale: 'Congl.Bituminoso di tipo Tappeto', prezzoUnitario: 72.00, unitaMisura: 'tn' },
    { nomeMateriale: 'Congl.Bituminoso di tipo Tappeto Hard', prezzoUnitario: 90.00, unitaMisura: 'tn' },
    { nomeMateriale: 'Congl.Bituminoso di tipo Sub-Ballast', prezzoUnitario: 66.00, unitaMisura: 'tn' },
    { nomeMateriale: 'Trasporto di fresato presso nostro Impianto', prezzoUnitario: 6.00, unitaMisura: 'tn' },
    { nomeMateriale: 'Oneri di conferimento', prezzoUnitario: 4.00, unitaMisura: 'tn' }
  ];

  // Caricamento materiali da Firebase
  const loadMateriali = async () => {
    try {
      setIsLoading(true);
      const materialiQuery = query(
        collection(db, 'materialiFissi'),
        orderBy('nomeMateriale', 'asc')
      );
      
      const snapshot = await getDocs(materialiQuery);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setMateriali(data);
      console.log(`Caricati ${data.length} materiali a costo fisso`);
    } catch (error) {
      console.error("Errore nel caricamento materiali:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Importazione massiva dei materiali iniziali
  const importMaterialiIniziali = async () => {
    try {
      setIsLoading(true);
      console.log('Avvio importazione materiali a costo fisso...');
      
      const batch = [];
      for (const materiale of materialiIniziali) {
        const materialeData = {
          ...materiale,
          createdAt: new Date(),
          updatedAt: new Date()
        };
        batch.push(addDoc(collection(db, 'materialiFissi'), materialeData));
      }
      
      await Promise.all(batch);
      console.log(`Importati ${materialiIniziali.length} materiali a costo fisso`);
      
      // Ricarica i dati
      await loadMateriali();
      setShowBulkImport(false);
    } catch (error) {
      console.error("Errore nell'importazione materiali:", error);
      alert('Errore durante l\'importazione dei materiali');
    } finally {
      setIsLoading(false);
    }
  };

  // Applicazione filtri
  const applyFilters = () => {
    let filtered = [...materiali];

    // Filtro per ricerca testuale
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(materiale => 
        materiale.nomeMateriale.toLowerCase().includes(search) ||
        materiale.unitaMisura.toLowerCase().includes(search)
      );
    }

    setFilteredMateriali(filtered);
  };

  // Reset del form
  const resetForm = () => {
    setFormData({
      nomeMateriale: '',
      prezzoUnitario: '',
      unitaMisura: 'tn'
    });
    setEditingId(null);
    setShowForm(false);
  };

  // Gestione input form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Salvataggio materiale
  const saveMateriale = async (e) => {
    e.preventDefault();
    
    if (!formData.nomeMateriale.trim()) {
      alert('Il nome del materiale è obbligatorio');
      return;
    }

    if (!formData.prezzoUnitario || parseFloat(formData.prezzoUnitario) <= 0) {
      alert('Il prezzo unitario è obbligatorio e deve essere maggiore di 0');
      return;
    }

    try {
      setIsLoading(true);
      
      const materialeData = {
        nomeMateriale: formData.nomeMateriale.trim(),
        prezzoUnitario: parseFloat(formData.prezzoUnitario),
        unitaMisura: formData.unitaMisura,
        updatedAt: new Date()
      };

      if (editingId) {
        // Aggiornamento
        const materialeRef = doc(db, 'materialiFissi', editingId);
        await updateDoc(materialeRef, materialeData);
        console.log('Materiale aggiornato:', editingId);
      } else {
        // Creazione
        materialeData.createdAt = new Date();
        await addDoc(collection(db, 'materialiFissi'), materialeData);
        console.log('Nuovo materiale creato');
      }

      resetForm();
      await loadMateriali();
    } catch (error) {
      console.error("Errore nel salvataggio materiale:", error);
      alert('Errore nel salvataggio del materiale');
    } finally {
      setIsLoading(false);
    }
  };

  // Modifica materiale
  const editMateriale = (materiale) => {
    setFormData({
      nomeMateriale: materiale.nomeMateriale,
      prezzoUnitario: materiale.prezzoUnitario?.toString() || '',
      unitaMisura: materiale.unitaMisura || 'tn'
    });
    setEditingId(materiale.id);
    setShowForm(true);
  };

  // Eliminazione materiale
  const deleteMateriale = async (materialeId, nomeMateriale) => {
    if (!window.confirm(`Sei sicuro di voler eliminare il materiale "${nomeMateriale}"?`)) {
      return;
    }

    try {
      setIsLoading(true);
      const materialeRef = doc(db, 'materialiFissi', materialeId);
      await deleteDoc(materialeRef);
      console.log('Materiale eliminato:', materialeId);
      await loadMateriali();
    } catch (error) {
      console.error("Errore nell'eliminazione materiale:", error);
      alert('Errore nell\'eliminazione del materiale');
    } finally {
      setIsLoading(false);
    }
  };

  // Caricamento iniziale
  useEffect(() => {
    loadMateriali();
  }, []);

  // Applicazione filtri quando cambiano i dati o i filtri
  useEffect(() => {
    applyFilters();
  }, [materiali, searchTerm]);

  // Utility per formattare il prezzo
  const formatPrice = (price, unitaMisura) => {
    return `€ ${price.toFixed(2)}/${unitaMisura}`;
  };

  // Ottieni l'etichetta dell'unità di misura
  const getUnitaMisuraLabel = (unitaMisura) => {
    const option = unitaMisuraOptions.find(opt => opt.value === unitaMisura);
    return option ? option.label : unitaMisura;
  };

  if (isLoading && materiali.length === 0) {
    return (
      <div className="master-data-container">
        <div className="loading-message">
          <div className="spinner"></div>
          <p>Caricamento materiali a costo fisso...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="master-data-container">
      <div className="master-data-header">
        <div className="header-left">
          <h2>🧱 Gestione Materiali Costo Fisso</h2>
          <p className="header-description">
            Gestisci i materiali con prezzo fisso per unità di misura (tonnellata, kilo, litro)
          </p>
        </div>
        <div className="header-actions">
          <button 
            className="btn btn-primary"
            onClick={() => setShowForm(true)}
            disabled={isLoading}
          >
            <span>➕</span>
            Nuovo Materiale
          </button>
          {materiali.length === 0 && (
            <button 
              className="btn btn-secondary"
              onClick={() => setShowBulkImport(true)}
              disabled={isLoading}
            >
              <span>📥</span>
              Importa Lista
            </button>
          )}
        </div>
      </div>

      {/* Sezione filtri e ricerca */}
      <div className="master-data-filters">
        <div className="search-box">
          <input
            type="text"
            placeholder="🔍 Cerca materiali..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filter-info">
          <span className="results-count">
            {filteredMateriali.length} di {materiali.length} materiali
          </span>
        </div>
      </div>

      {/* Lista materiali */}
      <div className="master-data-content">
        {filteredMateriali.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🧱</div>
            <h3>Nessun materiale trovato</h3>
            <p>
              {materiali.length === 0 
                ? 'Non ci sono ancora materiali a costo fisso. Inizia aggiungendone uno.'
                : 'Nessun materiale corrisponde ai filtri di ricerca.'
              }
            </p>
            {materiali.length === 0 && (
              <button 
                className="btn btn-primary"
                onClick={() => setShowForm(true)}
              >
                <span>➕</span>
                Aggiungi Primo Materiale
              </button>
            )}
          </div>
        ) : (
          <div className="master-data-table">
            <table>
              <thead>
                <tr>
                  <th>Nome Materiale</th>
                  <th>Prezzo Unitario</th>
                  <th>Unità di Misura</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filteredMateriali.map(materiale => (
                  <tr key={materiale.id}>
                    <td>
                      <strong>{materiale.nomeMateriale}</strong>
                    </td>
                    <td>
                      <span className="price-badge">
                        {formatPrice(materiale.prezzoUnitario, materiale.unitaMisura)}
                      </span>
                    </td>
                    <td>
                      <span className="unit-badge">
                        {getUnitaMisuraLabel(materiale.unitaMisura)}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon btn-edit"
                          onClick={() => editMateriale(materiale)}
                          title="Modifica materiale"
                          disabled={isLoading}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                          </svg>
                        </button>
                        <button
                          className="btn-icon btn-delete"
                          onClick={() => deleteMateriale(materiale.id, materiale.nomeMateriale)}
                          title="Elimina materiale"
                          disabled={isLoading}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            <line x1="10" y1="11" x2="10" y2="17"></line>
                            <line x1="14" y1="11" x2="14" y2="17"></line>
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Form di inserimento/modifica */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>
                {editingId ? '✏️ Modifica Materiale' : '➕ Nuovo Materiale'}
              </h3>
              <button 
                className="modal-close"
                onClick={resetForm}
                type="button"
              >
                ✕
              </button>
            </div>

            <form onSubmit={saveMateriale}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Nome Materiale *</label>
                    <input
                      type="text"
                      name="nomeMateriale"
                      value={formData.nomeMateriale}
                      onChange={handleInputChange}
                      placeholder="Es. Congl.Bituminoso di tipo Base"
                      required
                      disabled={isLoading}
                    />
                  </div>

                  <div className="form-group">
                    <label>Prezzo Unitario (€) *</label>
                    <input
                      type="number"
                      name="prezzoUnitario"
                      value={formData.prezzoUnitario}
                      onChange={handleInputChange}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                      required
                      disabled={isLoading}
                    />
                  </div>

                  <div className="form-group">
                    <label>Unità di Misura</label>
                    <select
                      name="unitaMisura"
                      value={formData.unitaMisura}
                      onChange={handleInputChange}
                      disabled={isLoading}
                    >
                      {unitaMisuraOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={resetForm}
                  disabled={isLoading}
                >
                  Annulla
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={isLoading}
                >
                  {isLoading ? 'Salvataggio...' : (editingId ? 'Aggiorna' : 'Salva')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal di importazione massiva */}
      {showBulkImport && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>📥 Importa Lista Materiali</h3>
              <button 
                className="modal-close"
                onClick={() => setShowBulkImport(false)}
                type="button"
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="import-preview">
                <p>Verranno importati i seguenti {materialiIniziali.length} materiali:</p>
                
                <div className="import-list">
                  {materialiIniziali.map((materiale, index) => (
                    <div key={index} className="import-item">
                      <strong>{materiale.nomeMateriale}</strong>
                      <span className="import-price">
                        {formatPrice(materiale.prezzoUnitario, materiale.unitaMisura)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={() => setShowBulkImport(false)}
                disabled={isLoading}
              >
                Annulla
              </button>
              <button 
                type="button" 
                className="btn btn-primary"
                onClick={importMaterialiIniziali}
                disabled={isLoading}
              >
                {isLoading ? 'Importazione...' : 'Conferma Importazione'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaterialiFissiManagement;